import { useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { AuthUser } from '@nargus/shared';
import { LoaderCircle, Trash2, Upload } from 'lucide-react';
import { useRemoveAvatar, useSetAvatar } from '@/resources/auth/auth.service';
import { MAX_AVATAR_BYTES } from '@/lib/files';
import { PushSettings } from '@/push/push-settings';
import { UserAvatar } from '@/components/user-avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ErrorNote, SectionHeader } from './ui';

const DATE_TIME = new Intl.DateTimeFormat('de-DE', {
  dateStyle: 'long',
  timeStyle: 'short',
});

export function AccountSection({ user }: { user: AuthUser }) {
  const { t } = useTranslation();

  const rows: Array<[string, string]> = [
    [t('account.name'), user.name ?? t('common.notProvided')],
    [t('account.email'), user.email],
    [t('account.registered'), DATE_TIME.format(new Date(user.createdAt))],
    [t('account.id'), user.id],
  ];

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader title={t('account.title')} hint={t('account.hint')} />

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle>{t('account.cardTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          <AvatarField user={user} />

          <dl className="flex flex-col gap-3">
            {rows.map(([label, value]) => (
              <div key={label} className="flex flex-col gap-0.5 sm:flex-row sm:gap-4">
                <dt className="text-sm text-muted-foreground sm:w-28 sm:shrink-0">{label}</dt>
                <dd className="text-sm break-all">{value}</dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      <PushSettings />
    </div>
  );
}

function AvatarField({ user }: { user: AuthUser }) {
  const { t } = useTranslation();
  const picker = useRef<HTMLInputElement>(null);
  const [tooLarge, setTooLarge] = useState(false);
  const set = useSetAvatar();
  const remove = useRemoveAvatar();
  const busy = set.isPending || remove.isPending;

  function onPick(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setTooLarge(file.size > MAX_AVATAR_BYTES);
    if (file.size > MAX_AVATAR_BYTES) return;
    set.mutate(file);
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-4">
        <UserAvatar
          size="lg"
          className="size-16"
          userId={user.id}
          name={user.name}
          email={user.email}
          avatarUpdatedAt={user.avatarUpdatedAt}
        />

        <div className="flex flex-wrap gap-2">
          <input
            id="account-avatar"
            ref={picker}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/avif,image/gif"
            className="hidden"
            onChange={onPick}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => picker.current?.click()}
          >
            {set.isPending ? <LoaderCircle className="size-4 animate-spin" /> : <Upload />}
            {user.avatarUpdatedAt ? t('account.avatarChange') : t('account.avatarAdd')}
          </Button>

          {user.avatarUpdatedAt && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => remove.mutate(undefined)}
            >
              {remove.isPending ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : (
                <Trash2 />
              )}
              {t('account.avatarRemove')}
            </Button>
          )}
        </div>
      </div>

      <p className="text-xs text-muted-foreground">{t('account.avatarHint')}</p>
      {tooLarge && <p className="text-sm text-destructive">{t('account.avatarTooLarge')}</p>}
      <ErrorNote error={set.error ?? remove.error} />
    </div>
  );
}
